import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import DNABox from '@alucio/lux-ui/src/components/layout/DNABox/DNABox';
import { Iffy } from '@alucio/lux-ui';
import MyUpdates from './Widgets/MyUpdates/MyUpdates';
import RecommendedActions from './Widgets/RecommendedActions/RecommendedActions';
import Documents, { DocumentWidgetVariant } from './Widgets/Documents/Documents';
import Folders, { FoldersWidgetVariant } from './Widgets/Folders/Folders';
import QuickFiltersWrapper from 'src/components/QuickFilters';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { TitleBar } from 'src/components/DNA';
import format from 'date-fns/format';
import { BulletinWidget } from 'src/screens/Bulletin/BulletinWidget';
import { useBulletins } from 'src/screens/Bulletin/useBulletins';
import Favorites from './Widgets/Favorites/Favorites';

const styles = StyleSheet.create({
  contentWrapper: {
    columnGap: 24,
    minWidth: 400,
    rowGap: 24,
    flexWrap: 'wrap-reverse',
  },
  leftSideContainer: {
    flex: 2,
    minWidth: 400,
    rowGap: 24,
  },
  fullWidthContainer: {
    flex: 1,
    rowGap: 24,
  },
  rightSideContainer: {
    flex: 1,
    minWidth: 320,
    rowGap: 24,
  },
});

const Home: React.FC = () => {
  const {
    BEAC_8524_beacon_bulletin,
    enableDemoMode,
    enableHomeRecentlyViewedDocsWidget,
    enableHomeMostViewedDocsWidget,
    enableHomeQuickFiltersWidget,
    enableHomeMyUpdatesWidget,
    enableHomeRecommendedActionsWidget,
    enableHomeRecentlyViewedFoldersWidget,
    enableHomeFavoriteFiles,
    enableHomeFavoriteFolders,
  } = useFeatureFlags(
    'BEAC_8524_beacon_bulletin',
    'enableDemoMode',
    'enableHomeRecentlyViewedDocsWidget',
    'enableHomeMostViewedDocsWidget',
    'enableHomeQuickFiltersWidget',
    'enableHomeMyUpdatesWidget',
    'enableHomeRecommendedActionsWidget',
    'enableHomeRecentlyViewedFoldersWidget',
    'enableHomeFavoriteFiles',
    'enableHomeFavoriteFolders',
  );

  const { canRead } = useBulletins();
  const showBulletinWidget = BEAC_8524_beacon_bulletin && canRead;
  const showFavoritesWidget = enableHomeFavoriteFiles || enableHomeFavoriteFolders;

  const hasRightSideContent =
    showBulletinWidget ||
    enableHomeQuickFiltersWidget ||
    enableHomeMyUpdatesWidget ||
    enableHomeRecommendedActionsWidget ||
    enableHomeRecentlyViewedFoldersWidget ||
    showFavoritesWidget;

  const currentDate = useMemo(
    () => format(new Date(), 'EEEE, LLLL d'),
    [],
  );

  return (
    <DNABox appearance="col">
      {/* TITLE */}
      <TitleBar
        title="Scientific Exchange Dashboard"
        description={currentDate}
      />
      {/* CONTENT */}
      <DNABox style={styles.contentWrapper}>
        {/* LEFT SIDE */}
        <DNABox
          appearance="col"
          style={hasRightSideContent ? styles.leftSideContainer : styles.fullWidthContainer}
        >
          <Iffy is={enableHomeRecentlyViewedDocsWidget}>
            <Documents variant={DocumentWidgetVariant.RECENTLY_VIEWED} />
          </Iffy>
          <Iffy is={enableHomeMostViewedDocsWidget}>
            <Documents
              variant={enableDemoMode ? DocumentWidgetVariant.TEAM_MOST_VIEWED : DocumentWidgetVariant.MOST_VIEWED}
            />
          </Iffy>
        </DNABox>
        {/* RIGHT SIDE */}
        <Iffy is={hasRightSideContent}>
          <DNABox appearance="col" style={styles.rightSideContainer}>
            <Iffy is={enableHomeQuickFiltersWidget}>
              <QuickFiltersWrapper ui2 />
            </Iffy>
            <Iffy is={showBulletinWidget}><BulletinWidget /></Iffy>
            <MyUpdates />
            <RecommendedActions />
            <Iffy is={showFavoritesWidget}>
              <Favorites />
            </Iffy>
            <Folders variant={FoldersWidgetVariant.RECENTLY_VIEWED} />
          </DNABox>
        </Iffy>
      </DNABox>
    </DNABox>
  );
};

export default Home;
